import { Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AccountInfo, InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { BehaviorSubject } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { SESSION_EXPIRED_FLAG } from "./app-auth-guards";

const RENEW_CHECK_MS = 30 * 1000;
const RENEW_BEFORE_EXPIRY_S = 10 * 60;

@Injectable({
    providedIn: "root",
})
export class AuthService {
    // Observable holding current account info
    private currentAccountSubject = new BehaviorSubject<AccountInfo | null>(
        null
    );
    currentAccount$ = this.currentAccountSubject.asObservable();
    private user: AccountInfo = null;

    get userInfo() {
        return this.currentAccountSubject.asObservable();
    }

    get isUserLoggedIn() {
        return this.userInfo.pipe(
            map(
                (user) => user != null && this.validateToken(user.idTokenClaims)
            )
        );
    }

    validateToken(idTokenClaims: any): boolean {
        if (!idTokenClaims || !idTokenClaims.exp) {
            return false;
        }

        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        return idTokenClaims.exp > currentTime;
    }

    constructor(
        private msalService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private router: Router,
        private zone: NgZone
    ) {
        // On service init, load any cached accounts
        this.loadAccount();

        // Listen for interaction status changes (login, logout, token acquisition)
        this.msalBroadcastService.inProgress$
            .pipe(filter((status) => status === InteractionStatus.None))
            .subscribe(() => {
                this.loadAccount();
            });

        this.zone.runOutsideAngular(() => {
            setInterval(() => this.renewIfExpiring(), RENEW_CHECK_MS);
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") {
                    this.renewIfExpiring();
                }
            });
        });
    }

    private renewing = false;

    // Renews tokens in the background shortly before the ID token expires, so the session
    // (menu, avatar, role check) never lapses while the user is still working.
    private renewIfExpiring(): void {
        const account = this.getCurrentAccount();
        const exp = account?.idTokenClaims?.exp;
        if (!account || !exp || this.renewing || this.router.url.startsWith("/login")) {
            return;
        }
        if (exp - Math.floor(Date.now() / 1000) > RENEW_BEFORE_EXPIRY_S) {
            return;
        }
        this.renewing = true;
        this.msalService
            .acquireTokenSilent({
                scopes: [...environment.protectedResources.api.scopes],
                account,
                forceRefresh: true,
            })
            .subscribe({
                next: () => {
                    this.renewing = false;
                    this.zone.run(() => this.loadAccount());
                },
                error: (err) => {
                    this.renewing = false;
                    // Network hiccups are retried on the next check; only a real auth failure ends the session.
                    if (err instanceof InteractionRequiredAuthError) {
                        this.zone.run(() => this.sessionExpired());
                    }
                },
            });
    }

    private sessionExpired(): void {
        sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1");
        this.router.navigate(["/login"], { queryParams: { returnUrl: this.router.url } });
    }

    private loadAccount() {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
            // Choose the first account (customize if needed)
            const activeAccount = accounts[0];
            this.msalService.instance.setActiveAccount(activeAccount);
            this.user = activeAccount;
            this.currentAccountSubject.next(activeAccount);
            console.log("Active account set:", activeAccount);
        } else {
            this.msalService.instance.setActiveAccount(null);
            this.user = null;
            this.currentAccountSubject.next(null);
            console.log("No active account found");
        }
    }

    // Helper method to get current account synchronously
    getCurrentAccount(): AccountInfo | null {
        return this.currentAccountSubject.value;
    }

    login(returnUrl?: string) {
        const target = returnUrl && returnUrl.startsWith("/") ? returnUrl : "/dashboard";
        this.msalService
            .initialize()
            .pipe(
                switchMap(() =>
                    this.msalService.loginRedirect({
                        scopes: [...environment.protectedResources.api.scopes],
                        redirectStartPage: window.location.origin + target,
                    })
                )
            )
            .subscribe();
    }

    logout() {
        this.msalService.logoutRedirect({
            account: this.getCurrentAccount() ?? undefined,
        });
    }

    isLoggedIn(): boolean {
        return this.getCurrentAccount() != null;
    }

    get isUserAuthorized$() {
        return this.userInfo.pipe(map(this.isUserAuthorizedInternal));
    }

    private isUserAuthorizedInternal(account: AccountInfo) {
     const roles = account?.idTokenClaims?.roles;
    return Array.isArray(roles) && roles.length > 0;
    }

    isUserAuthorized(): boolean {
        return this.isUserAuthorizedInternal(this.user);
    }

}
